import React, { useState } from 'react';
import Image from '@components/AppImage';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';

const DoctorCard = ({ doctor, onBookAppointment, onViewProfile }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  return (
    <div className="relative bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden group border border-blue-100">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-r from-[#08005f]/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-l from-[#eb2227]/5 to-transparent rounded-full translate-y-12 -translate-x-12"></div>
      
      {/* Doctor Image & Video */}
      <div className="relative h-72 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#08005f]/20 to-transparent z-10"></div>
        <Image
          src={doctor.image}
          alt={`Dr. ${doctor.name} - ${doctor.specialty}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';
          }}
        />

        {/* Experience Badge */}
        {doctor.experience > 0 && (
          <div className="absolute top-4 left-4 bg-gradient-to-r from-[#eb2227] to-[#ff6b6b] text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg z-20 flex items-center">
            <Icon name="Star" size={14} className="mr-1" />
            {doctor.experience}+ Years
          </div>
        )}

        {/* Language Badges */}
        {doctor.languages && doctor.languages.length > 0 && (
          <div className="absolute top-4 right-4 flex flex-wrap gap-1 z-20">
            {doctor.languages.slice(0, 3).map((language, index) => (
              <span
                key={index}
                className="bg-white/95 text-[#08005f] px-2.5 py-1 rounded-full text-xs font-medium shadow-md backdrop-blur-sm flex items-center"
              >
                <Icon name="Globe" size={10} className="mr-1" />
                {language}
              </span>
            ))}
            {doctor.languages.length > 3 && (
              <span className="bg-white/95 text-[#08005f] px-2.5 py-1 rounded-full text-xs font-medium shadow-md">
                +{doctor.languages.length - 3}
              </span>
            )}
          </div>
        )}
        
        {/* Overlay content at bottom of image */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-20 bg-gradient-to-t from-[#08005f]/25 to-transparent pt-10">
          <h3 className="font-heading font-bold text-xl text-white mb-1 truncate">
            {doctor.name}
          </h3>
          <p className="text-white/90 font-medium text-sm">{doctor.specialty}</p>
        </div>
      </div>

      {/* Doctor Information */}
      <div className="p-6 relative">
        {/* Specialization Badges */}
        {doctor.specializations && doctor.specializations.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {doctor.specializations.map((spec, index) => (
              <span
                key={index}
                className="bg-gradient-to-r from-[#08005f]/10 to-[#eb2227]/10 text-[#08005f] px-3 py-1.5 rounded-full text-xs font-medium border border-[#08005f]/10"
              >
                {spec}
              </span>
            ))}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {doctor.rating && (
            <div className="text-center p-2 bg-blue-50 rounded-lg">
              <div className="flex justify-center items-center text-[#eb2227] mb-1">
                <Icon name="Star" size={14} className="fill-current" />
                <span className="text-sm font-bold ml-1">{doctor.rating}</span>
              </div>
              <p className="text-xs text-gray-600">Rating</p>
            </div>
          )}
          
          {doctor.patients && (
            <div className="text-center p-2 bg-blue-50 rounded-lg">
              <div className="text-[#08005f] font-bold text-sm mb-1">
                {doctor.patients}+
              </div>
              <p className="text-xs text-gray-600">Patients</p>
            </div>
          )}
          
          {doctor.reviews && (
            <div className="text-center p-2 bg-blue-50 rounded-lg">
              <div className="text-[#08005f] font-bold text-sm mb-1">
                {doctor.reviews}
              </div>
              <p className="text-xs text-gray-600">Reviews</p>
            </div>
          )}
        </div>

        {/* Credentials */}
        {doctor.credentials && doctor.credentials.length > 0 && (
          <div className="mb-5">
            <h4 className="font-heading font-semibold text-sm text-[#08005f] mb-3 flex items-center">
              <Icon name="GraduationCap" size={16} className="mr-2 text-[#eb2227]" />
              Education & Training
            </h4>
            <ul className="space-y-2">
              {doctor.credentials.slice(0, 2).map((credential, index) => (
                <li key={index} className="text-gray-700 text-sm flex items-start">
                  <Icon name="CheckCircle" size={14} className="mr-2 mt-0.5 text-[#eb2227] flex-shrink-0" />
                  <span>{credential}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="outline"
            className="flex-1 border-[#08005f] text-[#08005f] hover:bg-[#08005f] hover:text-white transition-colors"
            iconName="User"
            iconPosition="left"
            iconSize={16}
            onClick={() => onViewProfile(doctor)}
          >
            View Full Profile
          </Button>
        </div>
      </div>

      {/* Availability indicator */}
      {doctor.available && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full ring-2 ring-white"></div>
      )}
    </div>
  );
};

export default DoctorCard;